export class StreamClient {
    private socket: WebSocket;
    private token: string;
    private uri: string;
    private authenticated: boolean;
    private subscribed: boolean;
    private retryInterval = 1000;
    private retryIntervalMax = 5000;

    constructor(uri: string, token: string) {
        this.uri = uri;
        this.token = token;
    }

    public writeText: any;
    public appendText: any;

    async pull(eventId: string, language: string, uppercase: boolean = false): Promise<void> {
        return new Promise((resolve, err) => {
            if (this.socket) {
                this.closeSocket();
            }

            this.socket = new WebSocket(this.uri);

            this.socket.onmessage = (msg) => {
                const message = msg.data.split(':');

                if (!this.authenticated && this.authenticate(message)) {
                    this.sendRequest(`pull: ${eventId} ${language} ${uppercase}`);
                    return;
                }

                if (message[0] === 'data') {
                    if (this.writeText)
                        this.writeText(msg.data.substring(6));
                    console.log('WebSocket pull successful');
                    this.closeSocket();
                    resolve();
                    return;
                }

                console.log(`Unrecognized WebSocket message: ${msg}`);
            }

            this.socket.onopen = () => {
                this.socket.send(`auth: ${this.token}`);
            }

            this.socket.onerror = (error) => {
                console.log(`Error opening Websocket: ${error}`);
                err(error);
            }
        });
    }

    subscribe(eventId: string, language: string) {
        return new Promise((resolve, err) => {
            if (this.socket) {
                this.closeSocket();
            }

            this.connectSocket();

            this.attachSubscriptionEvents(eventId, language);
        });
    }

    private attachSubscriptionEvents(eventId: string, language: string) {
        this.socket.onmessage = (msg) => {
            const message = msg.data.split(':');

            if (!this.authenticated && this.authenticate(message)) {
                this.sendRequest(`subscribe: ${eventId} ${language} ${this.getViewerId()}`);
                console.log('Stream subscribed');
                this.subscribed = true;
                return;
            }

            if (message[0] === 'data') {
                if (this.appendText)
                    this.appendText(msg.data.substring(6, msg.data.length - 2));
                return;
            }

            if (message[0] === 'ping') {
                console.log('Websocket pinged');
                this.sendRequest('pong:');
                return;
            }
            
            console.log(`Unrecognized WebSocket message: ${msg}`);
        }

        this.socket.onopen = () => {
            this.socket.send(`auth: ${this.token}`);
            this.retryInterval = 1000;
        }

        this.socket.onerror = (error) => {
            console.log(`Subscribe error opening Websocket: ${error}`);
            if (this.subscribed) {
                this.retrySubscription(eventId, language);
            }
        }

        this.socket.onclose = () => {
            console.log('Subscribe Websocket closed');
            if (this.subscribed) {
                this.retrySubscription(eventId, language);
            }
        }
    }

    private retrySubscription(eventId: string, language: string) {
        console.log('Connection unexpectedly closed, reconnecting');
        this.closeSocket();

        setTimeout(() => {
            this.connectSocket();
            this.attachSubscriptionEvents(eventId, language);
        }, this.retryInterval);

        this.retryInterval += 1000;
        this.retryInterval = this.retryInterval > this.retryIntervalMax ? this.retryIntervalMax : this.retryInterval;
    }

    async unsubscribe() {
        console.log('Stream unsubscribed');
        this.subscribed = false;
        await this.closeSocket();
    }

    private sendRequest(request: string) {
        this.socket.send(`${request}\n\n`);
    }

    private authenticate(message: string[]): boolean {
        if (message[0] !== 'auth') {
            console.log('Invalid authorization response for WebSocket');
            this.closeSocket();
            return false;
        }

        if (message[1].trim() !== 'OK') {
            console.log('Authorization failed for WebSocket');
            return false;
        }

        console.log('WebSocket authenticated');
        this.authenticated = true;
        return true;
    }

    private connectSocket() {
        this.socket = new WebSocket(this.uri);
    }

    private closeSocket() {
        this.authenticated = false;
        
        if (!this.socket)
            return;
        this.socket.onopen = null;
        this.socket.onerror = null;
        this.socket.onmessage = null;
        this.socket.onclose = null;
        this.socket.close();
    }

    private getViewerId(): string {
        let id = localStorage.getItem('viewerId');
        if (!id) {
            id = (crypto as any).randomUUID();
            localStorage.setItem('viewerId', id);
        }

        return id;
    }
}